import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";

import { columns } from "./columns";

//components
import CardTable from "components/CardTable/CardTable.jsx";
import { TableInputs } from "components/CardTable/TableInputs";
import RegularButton from "components/CustomButtons/Button";

//hooks
import { useSearch } from "hooks/useSearch";
import { exportExcelFile } from "helpers/exportExcelFile";
import { formatCustomDate } from "helpers/dateTools";

//icons
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import GetAppIcon from "@mui/icons-material/GetApp";

//actions
import {
  getNotesFlotilla,
  flotillaSearchInputs,
} from "redux/actions/notesFlotilla/notesFlotillaActions";

const tableKey = "notesFlotilla";

const notesFlotillaHeaders = {
  date: "Fecha",
  station: "Planta",
  unitName: "Unidad",
  plates: "Placas",
  prevMileage: "Km Anterior",
  mileage: "Km Actual",
  quantity: "Litros Surtidos",
  price: "Precio Unitario",
  total: "Total",
  customer: "Cliente",
};

const mapNotesFlotillaDownload = (notes) => {
  return (
    notes &&
    Object.values(notes).map((value) => {
      return {
        date: value.date || "Sin registro",
        station: value.station || "Sin registro",
        unitName: value.unitName || "Sin registro",
        plates: value.plates || "Sin registro",
        prevMileage: value.prevMileage || "Sin registro",
        mileage: value.mileage || "Sin registro",
        quantity: value.quantity || "Sin registro",
        price: value.price || "Sin registro",
        total: value.total || "Sin registro",
        customer: value.customer || "Sin registro",
      };
    })
  );
};

export const NotesFlotilla = () => {
  const dispatch = useDispatch();
  const { setSnackbar, buildTableConfig } = useSearch();
  const { notesFlotilla, station, startDate, endDate } = useSelector(
    (state) => state.notesFlotilla
  );

  const { isLoading, configTables } = useSelector((state) => state.search);
  const { stations } = useSelector(({ companies }) => companies);
  const { units } = useSelector(({ unitsReducer }) => unitsReducer);
  const [mapNotesFlotilla, setMapNotesFlotilla] = useState([]);

  useEffect(() => {
    !configTables[tableKey] && buildTableConfig(tableKey);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!station || station.length === 0) {
      setSnackbar("No se ha seleccionado alguna planta.");
      return;
    }
    const currentDay = dayjs();

    if (!startDate) {
      setSnackbar("No se ha seleccionado fecha de inicio.");
      return;
    }
    if (!endDate) {
      setSnackbar("No se ha seleccionado fecha final.");
      return;
    }
    if (!dayjs(startDate).isSameOrBefore(dayjs(endDate), "day")) {
      setSnackbar("La fecha final no puede ser menor a la inicial.");
      return;
    }
    if (dayjs(endDate).diff(dayjs(currentDay), "day") >= 1) {
      setSnackbar("La fecha final no puede ser mayor a un dia del dia actual");
      return;
    }
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    dispatch(getNotesFlotilla(station, start, end));
  }, [station, startDate, endDate]);

  const handleDownLoad = () => {
    const fileName = `NotasFlotilla-${formatCustomDate(
      startDate
    )}-${formatCustomDate(endDate)}.xlsx`;
    exportExcelFile(
      mapNotesFlotillaDownload(notesFlotilla),
      fileName,
      notesFlotillaHeaders
    );
  };

  useEffect(() => {
    if (notesFlotilla.length > 0) {
      const mapping = notesFlotilla.map((note) => {
        return {
          ...note,
          station: stations[units[note.idUnit].idStation].label,
          idStation: stations[units[note.idUnit].idStation].id,
        };
      });
      setMapNotesFlotilla(mapping);
    }
  }, [notesFlotilla, stations, units]);

  return (
    <CardTable
      columns={columns}
      data={mapNotesFlotilla}
      isLoading={isLoading}
      hasControl
      hasHeader
      icon={<TextSnippetIcon />}
      title="Notas de Flotilla"
      titleBlock
      blockBtn
      tableKey={tableKey}
      tableContent={
        <TableInputs
          reducer="notesFlotilla"
          dates
          companies
          multiStations
          stationsGridWidth={4}
          dispatchAction={flotillaSearchInputs}
          submitAction={handleSubmit}
          loading={isLoading}
        />
      }
      controls={
        <RegularButton
          onClick={handleDownLoad}
          color="info"
          justIcon
          round
          tooltip="Descargar"
          disabled={notesFlotilla.length === 0}
        >
          <GetAppIcon />
        </RegularButton>
      }
    />
  );
};
